import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import WorkCard from '@/components/feature/WorkCard';
import SearchBar from '@/components/feature/SearchBar';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  TYPE_META,
  REGION_OPTIONS,
  STATUS_OPTIONS,
  filterWorks,
  getWorksByType,
  getYearOptions,
} from '@/lib/works';
import type { Region, SubtitleStatus, WorkType } from '@/types/work';

const CONTAINER = 'mx-auto w-full max-w-[1240px] px-4 md:px-8 lg:px-10';
const PAGE_SIZE = 8;
const VALID_TYPES: WorkType[] = ['movie', 'tv', 'variety'];

type SortKey = 'updated' | 'popular' | 'year';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'updated', label: '更新时间降序' },
  { value: 'popular', label: '热度排序' },
  { value: 'year', label: '年份最新' },
];

const SELECT_CLASS =
  'h-11 w-full rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-800 transition-colors focus:border-primary-400 focus:outline-none cursor-pointer';

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const isValidType = VALID_TYPES.includes(type as WorkType);
  const workType = (isValidType ? type : 'movie') as WorkType;
  const meta = TYPE_META[workType];

  const [region, setRegion] = useState<Region | ''>('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [status, setStatus] = useState<SubtitleStatus | ''>('');
  const [sort, setSort] = useState<SortKey>('updated');
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  usePageMeta(
    isValidType
      ? `【${meta.label}】日韩中文字幕资源 - 最新日韩影视字幕`
      : '页面未找到 - 日韩中文字幕',
    `日韩${meta.label}中文字幕${meta.description}支持按地区、年份与字幕状态筛选。`
  );

  const years = useMemo(() => getYearOptions(), []);

  useEffect(() => {
    setPage(1);
  }, [type, region, yearFrom, yearTo, status, sort]);

  const filtered = useMemo(() => {
    if (!isValidType) return [];
    return filterWorks(getWorksByType(workType), {
      region: region || undefined,
      status: status || undefined,
      yearFrom: yearFrom ? Number(yearFrom) : undefined,
      yearTo: yearTo ? Number(yearTo) : undefined,
      sort,
    });
  }, [workType, isValidType, region, status, yearFrom, yearTo, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const hasFilter = Boolean(region || yearFrom || yearTo || status);

  const resetFilters = () => {
    setRegion('');
    setYearFrom('');
    setYearTo('');
    setStatus('');
    setSort('updated');
  };

  if (!isValidType) {
    return (
      <div className={`${CONTAINER} flex flex-col items-center py-24 text-center`}>
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
          <i className="ri-error-warning-line text-3xl"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">未找到该分类</h1>
        <p className="mt-2 text-sm text-foreground-500">请从电影、电视剧或综艺分类中选择浏览。</p>
        <Link
          to="/"
          className="mt-6 inline-flex h-11 items-center gap-2 rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <>
      <section className="border-b border-background-200 bg-background-100">
        <div className={`${CONTAINER} py-8 md:py-10`}>
          <Breadcrumb items={[{ label: '主页', to: '/' }, { label: meta.label }]} />
          <div className="mt-5 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="flex items-center gap-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className={`${meta.icon} text-xl`}></i>
                </span>
                日韩{meta.label}中文字幕
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
                {meta.description}
              </p>
            </div>
            <div className="w-full max-w-md">
              <SearchBar size="sm" placeholder={`搜索${meta.label}作品…`} />
            </div>
          </div>
        </div>
      </section>

      <section className={`${CONTAINER} py-8 md:py-10`}>
        <div className="mb-5 flex items-center justify-between gap-3">
          <p className="text-sm text-foreground-600">
            共找到 <strong className="font-semibold text-foreground-950">{filtered.length}</strong> 部作品
          </p>
          <button
            type="button"
            onClick={() => setFilterOpen((open) => !open)}
            className="inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm text-foreground-700 transition-colors hover:border-primary-400 cursor-pointer lg:hidden"
          >
            <i className="ri-filter-3-line"></i>
            筛选
          </button>
        </div>

        <div
          className={`${
            filterOpen ? 'block' : 'hidden'
          } mb-6 rounded-lg border border-background-200 bg-background-50 p-4 lg:block lg:mb-8`}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-500">地区</span>
              <select
                className={SELECT_CLASS}
                value={region}
                onChange={(event) => setRegion(event.target.value as Region | '')}
              >
                <option value="">全部地区</option>
                {REGION_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-500">年份从</span>
              <select className={SELECT_CLASS} value={yearFrom} onChange={(event) => setYearFrom(event.target.value)}>
                <option value="">不限</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-500">年份至</span>
              <select className={SELECT_CLASS} value={yearTo} onChange={(event) => setYearTo(event.target.value)}>
                <option value="">不限</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-500">字幕状态</span>
              <select
                className={SELECT_CLASS}
                value={status}
                onChange={(event) => setStatus(event.target.value as SubtitleStatus | '')}
              >
                <option value="">全部状态</option>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-500">排序</span>
              <select className={SELECT_CLASS} value={sort} onChange={(event) => setSort(event.target.value as SortKey)}>
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {hasFilter && (
            <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-4">
              <span className="text-xs text-foreground-500">已应用筛选条件</span>
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600 cursor-pointer"
              >
                <i className="ri-close-line"></i>
                清空筛选
              </button>
            </div>
          )}
        </div>

        {paged.length === 0 ? (
          <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
              <i className="ri-inbox-2-line text-3xl"></i>
            </span>
            <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">暂无相关字幕资源</h2>
            <p className="mt-2 max-w-sm text-sm text-foreground-500">
              当前筛选条件下没有匹配的作品，试试调整或清空筛选条件。
            </p>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-6 inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              清空筛选
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
              {paged.map((work) => (
                <WorkCard key={work.id} work={work} />
              ))}
            </div>

            {totalPages > 1 && (
              <nav className="mt-10 flex items-center justify-center gap-2" aria-label="分页">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="inline-flex h-10 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm text-foreground-700 transition-colors enabled:hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>
                  上一页
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                  <button
                    key={pageNumber}
                    type="button"
                    onClick={() => setPage(pageNumber)}
                    aria-current={pageNumber === currentPage ? 'page' : undefined}
                    className={`h-10 w-10 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                      pageNumber === currentPage
                        ? 'bg-primary-500 text-background-50'
                        : 'border border-background-300 text-foreground-700 hover:border-primary-400'
                    }`}
                  >
                    {pageNumber}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={currentPage === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="inline-flex h-10 items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-4 text-sm text-foreground-700 transition-colors enabled:hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                >
                  下一页
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </nav>
            )}
          </>
        )}
      </section>
    </>
  );
}